import {Calendar, List, Checkbox, Button, Input, DatePicker, Collapse, Select} from 'antd'
import React, {Children, useMemo, useRef, useState} from 'react'
import './todos.less'
import {useIntl} from 'react-intl'
import {CalendarMode} from 'antd/es/calendar/generateCalendar'
import dayjs, {Dayjs} from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import {
  CustomToDoType,
  getJournalEventTypeInfo,
  PredefinedToDoTypeId,
  PredefinedToDoTypes,
  ToDoTypeMetadata,
} from '../../entities/journalRecord'
import {
  DeleteOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons'
import {useIsMobileNarrow, useIsToDoFiltersNarrow} from '../../hooks/useMediaQuery'
import {ToDoPageFiltersSmall} from './ToDoPageFiltersSmall'
import {EmEmoji} from '../emoji/EmEmoji'
dayjs.extend(weekday)

const onPanelChange = (value: Dayjs, mode: CalendarMode) => {
  // console.log(value.format('YYYY-MM-DD'), mode)
}

const dateFormat = 'YYYY/MM/DD'

// A predefined type is filtered by its own name, a user-defined one by the id of
// the custom type behind it.
export type ToDoFilterTypeId = PredefinedToDoTypeId | number

export type ToDoFilters = {
  types: Partial<Record<ToDoFilterTypeId, boolean>>
  startDate?: Date
  endDate?: Date
  plants: Partial<Record<number, boolean>>
}

const toDoSortingFields = ['date', 'plants', 'taskType'] as const
export type ToDoSortingField = (typeof toDoSortingFields)[number]

export type ToDoPageFiltersProps = {
  plantedPlantsNamesWithId: {id: number; name: string}[]
  filters: ToDoFilters
  sortingField: ToDoSortingField
  setSortingField: (field: ToDoSortingField) => void
  sortingDirection: boolean
  setSortingDirection: (direction: boolean) => void
  setFilters: (filters: ToDoFilters) => void
  userToDoTypes: CustomToDoType[]
}

export function ToDoPageFilters(props: ToDoPageFiltersProps) {
  const intl = useIntl()
  const calendarRef = useRef<HTMLDivElement | null>(null)
  // the plants column drops off the row before the panel itself runs out of width
  const isNarrow = useIsToDoFiltersNarrow()
  const isMobileNarrow = useIsMobileNarrow()
  const [filtersCollapsed, setFiltersCollapsed] = useState(true)
  const plantNameLookup = useMemo(() => {
    // keyed by string because the ids come back out of Object.keys
    const lookup: Record<string, string> = {}
    for (const {id, name} of props.plantedPlantsNamesWithId) {
      lookup[id] = name
    }

    return lookup
  }, [props.plantedPlantsNamesWithId])

  const hasDateFilter = props.filters.startDate && props.filters.endDate
  const hasTypeFilter = Object.keys(props.filters.types).length > 0
  const hasPlantFilter = Object.keys(props.filters.plants).length > 0

  const hasFilters = hasDateFilter || hasTypeFilter || hasPlantFilter

  return (
    <Collapse
      className="todo__filters-collapse"
      bordered={false}
      activeKey={filtersCollapsed ? [] : ['0']}
      onChange={(k) => setFiltersCollapsed(k[0] === '0' ? false : true)}
      items={[
        {
          key: '0',
          label: (
            <div>
              <h4 style={{display: 'inline'}}>
                <FilterOutlined /> {intl.formatMessage({id: 'filtersAndSorting'})}
                {hasFilters && (
                  <span style={{marginLeft: 30, fontWeight: 400}}>
                    <a
                      onClick={(e) => {
                        props.setFilters({
                          plants: {},
                          types: {},
                          startDate: undefined,
                          endDate: undefined,
                        })
                        e.stopPropagation()
                      }}
                    >
                      <DeleteOutlined /> {intl.formatMessage({id: 'clearFilters'})}
                    </a>
                  </span>
                )}
              </h4>

              {filtersCollapsed && (
                <div>
                  {hasTypeFilter ? (
                    <div>
                      <em>{intl.formatMessage({id: 'taskTypes'})}:</em>{' '}
                      {Object.keys(props.filters.types)
                        .map((type) => intl.formatMessage({id: `eventType-${type}`}))
                        .join(', ')}
                    </div>
                  ) : null}
                  {hasDateFilter ? (
                    <div>
                      <em>{intl.formatMessage({id: 'date'})}:</em>{' '}
                      {dayjs(props.filters.startDate).format(dateFormat)} -{' '}
                      {dayjs(props.filters.endDate).format(dateFormat)}
                    </div>
                  ) : null}
                  {hasPlantFilter ? (
                    <div>
                      <em>{intl.formatMessage({id: 'plants'})}:</em>{' '}
                      {Object.keys(props.filters.plants)
                        .map((plantId) => plantNameLookup[plantId])
                        .join(', ')}
                    </div>
                  ) : null}
                  <div>
                    <em>{intl.formatMessage({id: 'sortingBy'})}:</em>{' '}
                    {intl.formatMessage({id: props.sortingField})}
                    {props.sortingDirection ? ' ↑' : ' ↓'}
                  </div>
                </div>
              )}
            </div>
          ),
          children: (
            <div>
              <div className="todo__filters__sorting">
                <em>{intl.formatMessage({id: 'sortBy'})}:</em>
                <div className="todo__filters__sorting__controls">
                  <Select
                    value={props.sortingField}
                    onChange={(value) => props.setSortingField(value as ToDoSortingField)}
                    options={toDoSortingFields.map((field) => ({
                      value: field,
                      label: intl.formatMessage({id: field}),
                    }))}
                  />
                  {isMobileNarrow ? (
                    // the icon carries the direction on its own, leaving the row's
                    // width to the field select - same control as the plants page
                    <Button
                      aria-label={intl.formatMessage({
                        id: props.sortingDirection ? 'sortingDirectionAsc' : 'sortingDirectionDesc',
                      })}
                      icon={
                        props.sortingDirection ? (
                          <SortAscendingOutlined />
                        ) : (
                          <SortDescendingOutlined />
                        )
                      }
                      onClick={() => props.setSortingDirection(!props.sortingDirection)}
                    />
                  ) : (
                    <Select
                      value={props.sortingDirection}
                      onChange={(value) => props.setSortingDirection(value as boolean)}
                      options={[
                        {
                          value: false,
                          label: intl.formatMessage({id: `sortingDirectionDesc`}),
                        },
                        {
                          value: true,
                          label: intl.formatMessage({id: `sortingDirectionAsc`}),
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
              {isNarrow ? (
                <ToDoPageFiltersSmall
                  plantedPlantsNamesWithId={props.plantedPlantsNamesWithId}
                  userToDoTypes={props.userToDoTypes}
                  filters={props.filters}
                  setFilters={props.setFilters}
                />
              ) : (
                <div className="todo__filters">
                  <div className="todo__filters__type">
                    <h4>{intl.formatMessage({id: 'taskTypes'})}:</h4>
                    <List
                      className="todo__filters__list"
                      itemLayout="horizontal"
                      dataSource={[
                        ...PredefinedToDoTypes.map((type) => ({
                          key: type,
                          name: intl.formatMessage({id: `eventType-${type}`}),
                          icon: ToDoTypeMetadata[type].icon,
                        })),
                        // a type the user deleted has no name left to filter by
                        ...props.userToDoTypes.flatMap(({customToDoType}) =>
                          customToDoType
                            ? [
                                {
                                  key: customToDoType.id,
                                  name: customToDoType.name,
                                  icon: customToDoType.icon,
                                },
                              ]
                            : []
                        ),
                      ]}
                      renderItem={(item) => (
                        <List.Item>
                          <div>
                            <Checkbox
                              checked={props.filters.types[item.key]}
                              onChange={(e) => {
                                const {checked} = e.target

                                props.setFilters({
                                  ...props.filters,
                                  types: {
                                    ...props.filters.types,
                                    [item.key]: checked,
                                  },
                                })
                              }}
                            >
                              <EmEmoji id={item.icon} size="1em" /> {item.name}
                            </Checkbox>
                          </div>
                        </List.Item>
                      )}
                    />
                  </div>

                  <div className="todo__filters__date">
                    {/* <Calendar
        className="todo__filters__date__calendar"
        fullscreen={false}
        onPanelChange={onPanelChange}
      /> */}

                    <div className="todo__filters__date__calendar" ref={calendarRef}>
                      <h4>{intl.formatMessage({id: 'date'})}:</h4>
                      <DatePicker.RangePicker
                        value={[
                          props.filters.startDate ? dayjs(props.filters.startDate) : null,
                          props.filters.endDate ? dayjs(props.filters.endDate) : null,
                        ]}
                        format={dateFormat}
                        open
                        getPopupContainer={() => calendarRef.current!}
                        classNames={{
                          popup: {
                            root: 'todo__filters__date__calendar__container__calendar',
                          },
                        }}
                        onChange={(values) =>
                          props.setFilters({
                            ...props.filters,
                            startDate: values?.[0]?.toDate() || undefined,
                            endDate: values?.[1]?.toDate() || undefined,
                          })
                        }
                        presets={[
                          {
                            label: intl.formatMessage({id: 'today'}),
                            value: [dayjs(), dayjs()],
                          },
                          {
                            label: intl.formatMessage({id: 'thisWeek'}),
                            value: [dayjs().weekday(0), dayjs().weekday(6)],
                          },
                          {
                            label: intl.formatMessage({id: 'thisMonth'}),
                            value: [dayjs().startOf('month'), dayjs().endOf('month')],
                          },
                        ]}
                      />
                      <div className="todo__filters__date__calendar__container" ref={calendarRef} />
                    </div>
                  </div>

                  <div className="todo__filters__plants">
                    <h4>{intl.formatMessage({id: 'plants'})}:</h4>
                    <List
                      className="todo__filters__list"
                      itemLayout="horizontal"
                      dataSource={props.plantedPlantsNamesWithId}
                      renderItem={(item) => (
                        <List.Item>
                          <div>
                            <Checkbox
                              checked={props.filters.plants[item.id]}
                              onChange={(e) =>
                                props.setFilters({
                                  ...props.filters,
                                  plants: {
                                    ...props.filters.plants,
                                    [item.id]: e.target.checked,
                                  },
                                })
                              }
                            >
                              {item.name}
                            </Checkbox>
                          </div>
                        </List.Item>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>
          ),
        },
      ]}
    />
  )
}
